import { memo } from 'react';

import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

import { DEFAULT_ZOOM, KEYBOARD_ZOOM_STEP, MAX_ZOOM, MIN_ZOOM } from '../constants';
import usePlannerStore from '../store';

const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

const toPercentage = (zoom: number) => `${Math.round(zoom * 100)}%`;

/**
 * Zoom controls for the planner timeline: zoom out / reset to default / zoom in.
 * Mirrors the bordered ghost-button group styling of the date switcher header.
 * The center button shows the current zoom as a percentage and resets to default
 * on click. Equivalent to the CMD/CTRL +/-/0 keyboard shortcuts in `useZoom`.
 */
export const PlannerZoomControl = memo(() => {
  const { t } = useTranslation('authenticated.planner');
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const setZoomLevel = usePlannerStore(store => store.setZoomLevel);

  const handleZoomOut = () => setZoomLevel(clampZoom(zoomLevel - KEYBOARD_ZOOM_STEP));
  const handleZoomIn = () => setZoomLevel(clampZoom(zoomLevel + KEYBOARD_ZOOM_STEP));
  const handleReset = () => setZoomLevel(DEFAULT_ZOOM);

  return (
    <div className="flex h-10 items-center rounded-md border border-input bg-background">
      <Button
        variant="ghost"
        size="sm"
        className="h-8"
        onClick={handleZoomOut}
        disabled={zoomLevel <= MIN_ZOOM}
        aria-label={t('zoom.zoomOut')}
      >
        <Minus size={16} />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-14 tabular-nums"
        onClick={handleReset}
        aria-label={t('zoom.resetZoom')}
      >
        {toPercentage(zoomLevel)}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-8"
        onClick={handleZoomIn}
        disabled={zoomLevel >= MAX_ZOOM}
        aria-label={t('zoom.zoomIn')}
      >
        <Plus size={16} />
      </Button>
    </div>
  );
});

PlannerZoomControl.displayName = 'PlannerZoomControl';
